/**
 * Feature flags, read from the API's `/feature-flags` through `@skillr/common`'s clients. The API
 * decides each flag for the signed-in caller (GW-14); the UI only asks.
 */
import { ApiClientError, FeatureFlagApiClient, FeatureFlagClient } from '@skillr/common'
import { useEffect, useSyncExternalStore } from 'react'

import { reportUnauthorized } from '@/shared/api/unauthorized'
import { useAuth } from '@/shared/auth/auth-context'
import { useSession } from '@/shared/auth/session'
import { SignInRenewalRequiredError } from '@/shared/auth/sign-in-hop'
import { useRuntimeConfig } from '@/shared/config/runtime-config-context'
import { log, logFailedApiCall, LogLevel } from '@/shared/logging'

/** The flags the API answered with: the ones enabled for the caller. */
type FeatureFlags = Parameters<typeof FeatureFlagClient.isEnabled>[0]

/** The API refreshes from App Configuration every 30 seconds, so a change shows within a minute. */
export const featureFlagRefreshMilliseconds = 30_000

const noFlags: FeatureFlags = []

let currentFlags: FeatureFlags = noFlags
const listeners = new Set<() => void>()

function publish(flags: FeatureFlags): void {
  currentFlags = flags
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function readFlags(): FeatureFlags {
  return currentFlags
}

/** Logs a failed refresh once, as a failed call only when a fetch made one. */
function reportRefreshFailure(error: unknown): void {
  if (error instanceof ApiClientError) {
    logFailedApiCall({ request: 'GET /feature-flags', status: error.status })
    // As the app's own API client does, so a signed-out tab or a lost session is noticed.
    if (error.status === 401) {
      reportUnauthorized()
    }
  } else if (error instanceof TypeError) {
    // What fetch throws when no answer arrives.
    logFailedApiCall({ request: 'GET /feature-flags', status: 0 })
  } else if (error instanceof SignInRenewalRequiredError) {
    // Nothing failed: the sign-in renews with the person's next request, and the flags after it.
  } else {
    // Before or after the request, such as getting a token, where no fetch saw it.
    log(
      LogLevel.Error,
      'The feature flags could not be refreshed.',
      error instanceof Error ? error : { error: String(error) },
    )
  }
}

/** Keeps the caller's flags current while someone is signed in. Mounted once, in `App.tsx`. */
export function FeatureFlagRefresh(): null {
  const { apiBaseUrl } = useRuntimeConfig()
  const { isAuthenticated, getAccessToken } = useAuth()
  const session = useSession()
  // The API knows the caller only once the site's session exists; each new one reads again.
  const caller = session.session?.userProfileId ?? null

  useEffect(() => () => publish(noFlags), [])

  useEffect(() => {
    if (!isAuthenticated || caller === null) {
      publish(noFlags)
      return undefined
    }

    const controller = new AbortController()
    const apiClient = new FeatureFlagApiClient({
      // Silent: a background poll never takes the person off the page to renew a sign-in.
      getAuthToken: async () => ({ accessToken: await getAccessToken({ isSilent: true }) }),
    })
    const url = `${apiBaseUrl.replace(/\/$/, '')}/feature-flags`
    const client = new FeatureFlagClient(apiClient, url, {
      // Every refresh asks the API; the interval below is the only cache.
      cacheDurationMs: 0,
      headers: { Accept: 'application/json', 'X-Site-BaseUrl': window.location.origin },
    })

    // One request at a time, so a slow answer can never overwrite a newer one.
    let isRefreshing = false
    const refresh = async () => {
      if (isRefreshing) {
        return
      }
      isRefreshing = true
      try {
        const answer = await client.getFeatureFlags({ signal: controller.signal })
        if (!controller.signal.aborted) {
          publish(Array.isArray(answer) ? answer : noFlags)
        }
      } catch (error: unknown) {
        // The last answer stands until the next one arrives.
        if (!controller.signal.aborted) {
          reportRefreshFailure(error)
        }
      } finally {
        isRefreshing = false
      }
    }

    void refresh()
    const timer = window.setInterval(() => void refresh(), featureFlagRefreshMilliseconds)

    return () => {
      controller.abort()
      window.clearInterval(timer)
    }
  }, [apiBaseUrl, isAuthenticated, caller, getAccessToken])

  return null
}

/**
 * Whether the flag is on for the signed-in caller. Off until the API has answered, and while
 * nobody is signed in.
 *
 * @param name - The flag's id in App Configuration.
 */
export function useFeatureFlag(name: string): boolean {
  return FeatureFlagClient.isEnabled(useSyncExternalStore(subscribe, readFlags), name)
}
